import '../../../i18n';
import {useTranslation} from 'react-i18next';
import React, { useRef, useMemo, useState, useEffect, useCallback } from 'react';
import ReactPlayer from 'react-player';
import { FiX } from 'react-icons/fi';
import { NOTE_BACKGROUND, PRIMARY_COLOR_8, PALE_WHITE_6, SECONDARY_COLOR } from '../../../appearance/Colors';
import { LAYOUT, isMobileViewport, calculateVideoFrameBounds } from '../layoutBreakpoints';
import { getMasonryVideoMuted, setMasonryVideoMuted, subscribeMasonryVideoMuted } from '../../../util/masonryVideoMutePreference';
import { useInViewVideoPlayback } from '../../../hooks/useInViewVideoPlayback';
import { setVideoPlaybackTime } from '../../../util/inViewVideoController';
import { VideoPlayOverlay } from './PlayIconOverlay';
import Duration from '../../Duration';
import { normalizeDurationToSeconds } from '../../../util/mediaDuration';

// Native video controls render inside the bottom strip of the element; clicking
// there should never be treated as a "toggle play/pause" body click, or it
// double-toggles whatever the native control button itself just did.
const CONTROL_BAR_HEIGHT_PX = 40;

const resolveVideoUrls = ({ url, file }) => {
    const preferredUrl = url || file?.cdn_url || file?.url || null;
    const fallbackUrl = file?.url && preferredUrl && file.url !== preferredUrl
        ? file.url
        : null;

    return {
        preferredUrl,
        fallbackUrl
    };
};

const buildVideoFileIdentity = file => {
    if (!file) {
        return 'none';
    }

    return [
        file.cdn_url || '',
        file.url || '',
        file.type || '',
        file.mime || '',
        file.format || '',
        file.width || '',
        file.height || '',
        file.aspect_ratio || '',
        file.duration || '',
        file.duration_seconds || '',
        file.media_duration || '',
        file.displayName || '',
        file.name || ''
    ].join('|');
};

const VideoSection = React.memo(function VideoSection({
    url,
    file,
    isUploading = false,
    uploadProgress = 0,
    uploadStatus = 'uploading',
    onCancelUpload,
    width,
    autoplayOnHover = true,
    openInWorkspaceOnly = false,
    onOpenVideoWorkspace,
    messageId
}) {
    const {t: lnT} = useTranslation();

    const wrapperRef = useRef();
    const playerRef = useRef(null);
    const { preferredUrl, fallbackUrl } = useMemo(
        () => resolveVideoUrls({ url, file }),
        [lnT, url, file?.cdn_url, file?.url]
    );
    const [activeUrl, setActiveUrl] = useState(preferredUrl);
    const [didUseFallback, setDidUseFallback] = useState(false);
    const [isMuted, setIsMuted] = useState(() => (autoplayOnHover ? getMasonryVideoMuted() : false));
    const [shouldPlay, setShouldPlay] = useState(false);

    const videoPlaybackId = useMemo(() => {
        const raw = activeUrl || preferredUrl || fallbackUrl || file?.key || file?.id || '';
        if (!raw) return '';
        return messageId ? `${messageId}-${raw}` : raw;
    }, [activeUrl, preferredUrl, fallbackUrl, file?.key, file?.id, messageId]);

    const {
        elementRef: inViewRef,
        shouldPlay: inViewShouldPlay,
        handleMouseEnter,
        handleMouseLeave
    } = useInViewVideoPlayback({
        id: videoPlaybackId,
        enabled: Boolean(autoplayOnHover && activeUrl && !isUploading)
    });

    const isPlaybackActive = autoplayOnHover ? inViewShouldPlay : shouldPlay;
    const [isHovered, setIsHovered] = useState(false);
    const [runtimeDuration, setRuntimeDuration] = useState(null);
    const [playedSeconds, setPlayedSeconds] = useState(0);

    const videoDurationSeconds = useMemo(() => {
        return normalizeDurationToSeconds(file?.duration || file?.duration_seconds || file?.media_duration || runtimeDuration);
    }, [file?.duration, file?.duration_seconds, file?.media_duration, runtimeDuration]);

    const remainingSeconds = useMemo(() => {
        if (!videoDurationSeconds) return 0;
        return Math.max(0, Math.ceil(videoDurationSeconds - playedSeconds));
    }, [videoDurationSeconds, playedSeconds]);

    useEffect(() => {
        if (!isPlaybackActive) {
            setPlayedSeconds(0);
        }
    }, [isPlaybackActive]);

    const handleWrapperMouseEnter = useCallback(event => {
        setIsHovered(true);
        handleMouseEnter?.(event);
    }, [handleMouseEnter]);

    const handleWrapperMouseLeave = useCallback(event => {
        setIsHovered(false);
        handleMouseLeave?.(event);
    }, [handleMouseLeave]);

    useEffect(() => {
        setActiveUrl(preferredUrl);
        setDidUseFallback(false);
        setShouldPlay(false);
    }, [preferredUrl, fallbackUrl]);

    // Mute preference is shared across every video on the page: unmuting one
    // unmutes all the others (and vice versa), live, not just for future ones.
    useEffect(() => {
        if (!autoplayOnHover) {
            return undefined;
        }

        setIsMuted(getMasonryVideoMuted());
        return subscribeMasonryVideoMuted(nextMuted => setIsMuted(nextMuted));
    }, [autoplayOnHover]);

    useEffect(() => {
        if (!autoplayOnHover) {
            return undefined;
        }

        let animationFrameId = null;
        let cleanupListener = null;

        const attachPlaybackListener = () => {
            const internalPlayer = playerRef.current?.getInternalPlayer?.();
            if (!(internalPlayer instanceof HTMLVideoElement)) {
                animationFrameId = window.requestAnimationFrame(attachPlaybackListener);
                return;
            }

            const onVolumeChange = () => {
                const nextMuted = Boolean(internalPlayer.muted || internalPlayer.volume === 0);
                setMasonryVideoMuted(nextMuted);
            };
            const onPlay = () => setShouldPlay(true);
            const onPause = () => setShouldPlay(false);

            internalPlayer.addEventListener('volumechange', onVolumeChange);
            internalPlayer.addEventListener('play', onPlay);
            internalPlayer.addEventListener('pause', onPause);
            cleanupListener = () => {
                internalPlayer.removeEventListener('volumechange', onVolumeChange);
                internalPlayer.removeEventListener('play', onPlay);
                internalPlayer.removeEventListener('pause', onPause);
            };
        };

        attachPlaybackListener();

        return () => {
            if (animationFrameId) {
                window.cancelAnimationFrame(animationFrameId);
            }
            if (typeof cleanupListener === 'function') {
                cleanupListener();
            }
        };
    }, [activeUrl, autoplayOnHover]);

    useEffect(() => {
        if (!autoplayOnHover || typeof document === 'undefined') {
            return undefined;
        }

        const handleVisibilityChange = () => {
            if (!document.hidden) {
                return;
            }

            setShouldPlay(false);

            const internalPlayer = playerRef.current?.getInternalPlayer?.();
            if (internalPlayer instanceof HTMLVideoElement) {
                internalPlayer.pause();
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [activeUrl, autoplayOnHover]);

    const handleOpenWorkspace = useCallback(event => {
        if (typeof onOpenVideoWorkspace !== 'function') {
            return;
        }

        event?.preventDefault?.();
        event?.stopPropagation?.();
        onOpenVideoWorkspace({
            src: activeUrl || preferredUrl || fallbackUrl || '',
            originalUrl: url || file?.url || '',
            hlsUrl: file?.hls_url || file?.media_hls_url || '',
            currentTime: playedSeconds || 0,
            autoPlay: true
        });
    }, [lnT, activeUrl, fallbackUrl, file?.hls_url, file?.media_hls_url, file?.url, onOpenVideoWorkspace, playedSeconds, preferredUrl, url]);

    const handleWrapperClick = useCallback(event => {
        if (openInWorkspaceOnly) {
            handleOpenWorkspace(event);
            return;
        }

        const internalPlayer = playerRef.current?.getInternalPlayer?.();
        if (!(internalPlayer instanceof HTMLVideoElement)) {
            return;
        }

        const rect = event.currentTarget.getBoundingClientRect();
        const clickedControlBar = event.clientY >= rect.bottom - CONTROL_BAR_HEIGHT_PX;
        if (clickedControlBar) {
            return;
        }

        if (autoplayOnHover && internalPlayer.muted) {
            internalPlayer.muted = false;
            setMasonryVideoMuted(false);

            if (internalPlayer.paused) {
                setShouldPlay(true);
                const playResult = internalPlayer.play();
                if (playResult?.catch) {
                    playResult.catch(error => {
                        console.debug('[VideoSection] Click-to-unmute autoplay was blocked:', error);
                    });
                }
            }
        }
    }, [lnT, autoplayOnHover, handleOpenWorkspace, openInWorkspaceOnly]);

    // Give desktop videos enough room for detail, while explicit widths and compact viewports stay bounded.
    const isCompactViewport = isMobileViewport();
    const {
        width: mediaFrameWidth,
        aspectRatio: validAspectRatio,
        resolvedVideoWidth
    } = calculateVideoFrameBounds(file, width, isCompactViewport);

    // In masonry, videos stay muted by default, but playback is fully manual.
    // We only mirror real play/pause state into ReactPlayer so native controls
    // are not fought and the app-level single-active-video listener can remain
    // the only coordinator.
    const playerConfig = useMemo(() => ({
        url: activeUrl,
        width: '100%',
        height: '100%',
        // Message lists are deliberately preview-only: playback belongs in the
        // workspace, where it has enough room and controls are not competing
        // with message selection/drag interactions.
        controls: !openInWorkspaceOnly,
        pip: true,
        playing: isPlaybackActive,
        muted: autoplayOnHover ? true : isMuted,
        loop: autoplayOnHover,
        playsinline: true,
        stopOnUnmount: false, // Prevent reload on unmount
        config: {
            file: {
                attributes: {
                    controlsList: 'nodownload',
                    playsInline: true,
                    loop: autoplayOnHover,
                    muted: autoplayOnHover,
                    // react-player merges this style object over its own width/height:100%
                    // (last key wins), so it must be repeated here or sizing breaks.
                    // object-fit: contain stops the video from stretching to the screen's
                    // aspect ratio (instead of its own, e.g. portrait/mobile) in native fullscreen.
                    style: { width: '100%', height: '100%', objectFit: 'contain' }
                }
            }
        }
    }), [lnT, activeUrl, autoplayOnHover, isMuted, isPlaybackActive, openInWorkspaceOnly]);

    const handlePlayerError = useCallback(() => {
        if (fallbackUrl && !didUseFallback && activeUrl !== fallbackUrl) {
            setActiveUrl(fallbackUrl);
            setDidUseFallback(true);
        }
    }, [lnT, activeUrl, fallbackUrl, didUseFallback]);

    // Show progress bar if uploading and no URL yet
    if (isUploading && !activeUrl) {
        return (
            <div
                ref={wrapperRef}
                style={{
                    position: 'relative',
                    borderRadius: 8,
                    backgroundColor: NOTE_BACKGROUND,
                    width: '100%',
                    maxWidth: validAspectRatio ? mediaFrameWidth : resolvedVideoWidth,
                    minWidth: 0,
                    ...(validAspectRatio ? {
                        aspectRatio: `1 / ${validAspectRatio}`,
                        height: 'auto',
                        overflow: 'hidden'
                    } : {
                        minHeight: 200,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                        padding: 16
                    })
                }}
            >
                <div
                    style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                        padding: 16
                    }}
                >
                <div style={{
                    width: '100%',
                    maxWidth: 300,
                    marginBottom: 12
                }}>
                    <div style={{
                        width: '100%',
                        height: 4,
                        backgroundColor: PALE_WHITE_6,
                        borderRadius: 2,
                        overflow: 'hidden',
                        marginBottom: 8
                    }}>
                        <div style={{
                            height: '100%',
                            width: `${Math.min(uploadProgress, 100)}%`,
                            backgroundColor: uploadStatus === 'error' ? '#ff4444' : PRIMARY_COLOR_8,
                            borderRadius: 2,
                            transition: 'width 0.15s ease-out'
                        }} />
                    </div>
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 8,
                        fontSize: 12,
                        color: SECONDARY_COLOR
                    }}>
                        <span>
                            {uploadStatus === 'error' ? lnT('messages:uploadFailed') :
                             uploadStatus === 'processing' ? lnT('messages:processing') :
                             Number(file?.size || 0) >= 2 * 1024 * 1024 * 1024
                                 ? lnT('messages:uploading', { value1: String(Number(uploadProgress || 0).toFixed(1)) })
                                 : lnT('messages:uploading', { value1: String(Math.round(Number(uploadProgress || 0))) })}
                        </span>
                        {typeof onCancelUpload === 'function' && uploadStatus !== 'error' ? (
                            <button
                                type="button"
                                title={lnT('messages:cancelUpload')}
                                aria-label={lnT('messages:cancelUpload')}
                                onClick={e => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    onCancelUpload();
                                }}
                                style={{
                                    background: 'none',
                                    border: 'none',
                                    cursor: 'pointer',
                                    padding: '2px 6px',
                                    color: SECONDARY_COLOR,
                                    fontSize: 11,
                                    borderRadius: 4,
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 3,
                                    opacity: 0.8,
                                    transition: 'opacity 0.15s ease, color 0.15s ease'
                                }}
                                onMouseEnter={e => {
                                    e.currentTarget.style.opacity = '1';
                                    e.currentTarget.style.color = '#ff4444';
                                }}
                                onMouseLeave={e => {
                                    e.currentTarget.style.opacity = '0.8';
                                    e.currentTarget.style.color = SECONDARY_COLOR;
                                }}
                            >
                                <FiX size={12} />
                                <span>{lnT('common:cancel')}</span>
                            </button>
                        ) : null}
                    </div>
                </div>
            </div>
            </div>
        );
    }

    if (activeUrl) {
        return (
            <div
                ref={node => {
                    wrapperRef.current = node;
                    inViewRef.current = node;
                }}
                onMouseEnter={handleWrapperMouseEnter}
                onMouseLeave={handleWrapperMouseLeave}
                onClick={handleWrapperClick}
                role={openInWorkspaceOnly ? 'button' : undefined}
                tabIndex={openInWorkspaceOnly ? 0 : undefined}
                onKeyDown={openInWorkspaceOnly ? event => {
                    if (event.key === 'Enter' || event.key === ' ') {
                        handleOpenWorkspace(event);
                    }
                } : undefined}
                aria-label={openInWorkspaceOnly ? lnT('messages:openVideoInWorkspace') : undefined}
                style={{
                    position: 'relative',
                    borderRadius: 8,
                    backgroundColor: NOTE_BACKGROUND,
                    width: '100%',
                    maxWidth: validAspectRatio ? mediaFrameWidth : resolvedVideoWidth,
                    minWidth: 0,
                    cursor: openInWorkspaceOnly ? 'pointer' : undefined,
                    ...(validAspectRatio ? {
                        aspectRatio: `1 / ${validAspectRatio}`,
                        height: 'auto',
                        overflow: 'hidden'
                    } : {
                        minHeight: 200,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center'
                    })
                }}
            >
                {openInWorkspaceOnly && !isPlaybackActive ? (
                    <VideoPlayOverlay isHovered={isHovered} />
                ) : null}
                {videoDurationSeconds && !isUploading ? (
                    <span
                        data-testid="video-duration-badge"
                        style={{
                            position: 'absolute',
                            bottom: 8,
                            left: 8,
                            zIndex: 3,
                            padding: '2px 6px',
                            borderRadius: 4,
                            backgroundColor: 'rgba(0, 0, 0, 0.65)',
                            backdropFilter: 'blur(4px)',
                            WebkitBackdropFilter: 'blur(4px)',
                            color: '#fff',
                            fontSize: 11,
                            fontWeight: 500,
                            lineHeight: '14px',
                            pointerEvents: 'none',
                            display: 'inline-flex',
                            alignItems: 'center',
                            letterSpacing: '0.02em',
                            fontVariantNumeric: 'tabular-nums'
                        }}
                    >
                        <Duration seconds={isPlaybackActive ? remainingSeconds : videoDurationSeconds} />
                    </span>
                ) : null}
                <div
                    style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        width: '100%',
                        height: '100%',
                        borderRadius: 8,
                        overflow: 'hidden',
                        opacity: isUploading ? 0.5 : 1,
                        transition: 'opacity 0.3s ease-in-out'
                    }}
                >
                    <ReactPlayer
                        ref={playerRef}
                        {...playerConfig}
                        onDuration={setRuntimeDuration}
                        onProgress={({ playedSeconds: p }) => {
                            setPlayedSeconds(p || 0);
                            setVideoPlaybackTime(videoPlaybackId, p || 0);
                        }}
                        onError={handlePlayerError}
                        style={{
                            width: '100%',
                            height: '100%'
                        }}
                    />
                </div>
                {isUploading && (
                    <div style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                        backgroundColor: 'rgba(0, 0, 0, 0.3)',
                        borderRadius: 8
                    }}>
                        <div style={{
                            width: '80%',
                            maxWidth: 300,
                            marginBottom: 12
                        }}>
                            <div style={{
                                width: '100%',
                                height: 4,
                                backgroundColor: 'rgba(255, 255, 255, 0.3)',
                                borderRadius: 2,
                                overflow: 'hidden',
                                marginBottom: 8
                            }}>
                                <div style={{
                                    height: '100%',
                                    width: `${Math.min(uploadProgress, 100)}%`,
                                    backgroundColor: uploadStatus === 'error' ? '#ff4444' : '#fff',
                                    borderRadius: 2,
                                    transition: 'width 0.15s ease-out'
                                }} />
                            </div>
                            <div style={{
                                textAlign: 'center',
                                fontSize: 12,
                                color: '#fff'
                            }}>
                                {uploadStatus === 'error' ? lnT('messages:uploadFailed') :
                                 uploadStatus === 'processing' ? lnT('messages:processing') :
                                 Number(file?.size || 0) >= 2 * 1024 * 1024 * 1024
                                     ? lnT('common:text', { value1: String(Number(uploadProgress || 0).toFixed(1)) })
                                     : lnT('common:text', { value1: String(Math.round(Number(uploadProgress || 0))) })}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        );
    }
    return null;
}, (previousProps, nextProps) => {
    return (
        previousProps.url === nextProps.url &&
        previousProps.width === nextProps.width &&
        previousProps.autoplayOnHover === nextProps.autoplayOnHover &&
        previousProps.openInWorkspaceOnly === nextProps.openInWorkspaceOnly &&
        previousProps.isUploading === nextProps.isUploading &&
        previousProps.uploadProgress === nextProps.uploadProgress &&
        previousProps.uploadStatus === nextProps.uploadStatus &&
        previousProps.onCancelUpload === nextProps.onCancelUpload &&
        buildVideoFileIdentity(previousProps.file) === buildVideoFileIdentity(nextProps.file)
    );
});

export default VideoSection;
